import React, { useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { SplashScreen } from './components/SplashScreen';
import { LoginScreen } from './components/LoginScreen';
import { BOPSignupFlow } from './components/BOPSignupFlow';
import { TermsScreen } from './components/TermsScreen';
import { SignupScreen } from './components/SignupScreen';
import { VerificationScreen } from './components/VerificationScreen';
import { OTPScreen } from './components/OTPScreen';
import { HomeScreen } from './components/HomeScreen';
import { PaymentsScreen } from './components/PaymentsScreen';
import { CardsScreen } from './components/CardsScreen';
import { RewardsScreen } from './components/RewardsScreen';
import { BottomNav } from './components/BottomNav';
import { TransactionsScreen } from './components/TransactionsScreen';
import { SendMoneyScreen } from './components/SendMoneyScreen';
import { MobileTopUpScreen } from './components/MobileTopUpScreen';
import { RaastScreen, RaastView } from './components/RaastScreen';
import { ProfileScreen } from './components/ProfileScreen';
import { MPINScreen, MPINMode, PinType } from './components/MPINScreen';
import { CoolingOffScreen } from './components/CoolingOffScreen';
import { ForgotPasswordScreen } from './components/ForgotPasswordScreen';
import { ZakaatScreen } from './components/ZakaatScreen';
import { LanguageProvider } from './utils/translations';
import { ThemeProvider, useTheme } from './utils/theme';
import { useScreenInit } from './useScreenInit.js';
type AppScreen = 'splash' | 'login' | 'bop-signup' | 'forgot-password' | 'mpin' | 'home' | 'payments' | 'cards' | 'rewards' | 'transactions' | 'send-money' | 'mobile-topup' | 'raast' | 'cooling-off' | 'profile' | 'more';
export function App() {
  return <ThemeProvider data-id="element-0">
      <AppContent data-id="element-1" />
    </ThemeProvider>;
}
function AppContent() {
  const {
    isDark
  } = useTheme();
  const screenInit = useScreenInit();
  const [currentScreen, setCurrentScreen] = useState<AppScreen>(screenInit.currentScreen as AppScreen ?? 'splash');
  const [mpinMode, setMpinMode] = useState<MPINMode>('enter');
  const [mpinType, setMpinType] = useState<PinType>('login');
  const [isNewUser, setIsNewUser] = useState(false);
  const [isMpinSet, setIsMpinSet] = useState(false);
  const [coolingOffUntil, setCoolingOffUntil] = useState<number | null>(null);
  const [signupUserType, setSignupUserType] = useState<'bop_account' | 'bop_credit_card' | null>(null);
  const [isCreditCardUser, setIsCreditCardUser] = useState(false);
  const handleSplashComplete = () => {
    setCurrentScreen('login');
  };
  const handleLogin = () => {
    if (coolingOffUntil && Date.now() < coolingOffUntil) {
      setCurrentScreen('cooling-off');
      return;
    }
    setIsNewUser(false);
    setCurrentScreen('home');
  };
  const handleNavigateToSignup = () => {
    setCurrentScreen('bop-signup');
  };
  const handleForgotPassword = () => {
    setCurrentScreen('forgot-password');
  };
  const handleSignupComplete = (userType?: 'bop_account' | 'bop_credit_card') => {
    setIsNewUser(true);
    setSignupUserType(userType ?? null);
    setCoolingOffUntil(Date.now() + 2 * 60 * 60 * 1000);
    setCurrentScreen('cooling-off');
  };
  const handleMPINComplete = () => {
    if (mpinType === 'mpin') {
      setIsMpinSet(true);
    }
    setCurrentScreen('home');
  };
  const handleMPINBack = () => {
    setCurrentScreen('home');
  };
  const handleLogout = () => {
    setIsNewUser(false);
    setIsMpinSet(false);
    setIsCreditCardUser(false);
    setSignupUserType(null);
    setCurrentScreen('splash');
  };
  const handleNavigateToTransactions = () => {
    setCurrentScreen('transactions');
  };
  const handleNavigateToSendMoney = () => {
    setCurrentScreen('send-money');
  };
  const handleNavigateToMobileTopUp = () => {
    setCurrentScreen('mobile-topup');
  };
  const [raastInitialView, setRaastInitialView] = useState<RaastView>('hub');
  const handleNavigateToRaast = () => {
    setRaastInitialView('hub');
    setCurrentScreen('raast');
  };
  const handleNavigateToRaastQR = () => {
    setRaastInitialView('qr_till');
    setCurrentScreen('raast');
  };
  const handleNavigateToProfile = () => {
    setCurrentScreen('profile');
  };
  const handleNavigateToMore = () => {
    setCurrentScreen('more');
  };
  const handleNavigate = (screen: 'home' | 'payments' | 'cards' | 'rewards') => {
    setCurrentScreen(screen);
  };
  const showBottomNav = currentScreen === 'home';
  const goHome = () => setCurrentScreen('home');
  return <LanguageProvider data-id="element-2">
      <div className={`w-full min-h-screen ${isDark ? 'bg-[#1A0E08]' : 'bg-[#FFF5F0]'}`} data-id="element-3">
        <div className={`max-w-[430px] mx-auto min-h-screen relative overflow-hidden ${isDark ? 'bg-[#1A0E08]' : 'bg-[#FFF5F0]'}`} data-id="element-4">
          <AnimatePresence mode="wait" data-id="element-5">
            {currentScreen === 'splash' && <SplashScreen key="splash" onComplete={handleSplashComplete} data-id="element-6" />}
            {currentScreen === 'login' && <LoginScreen key="login" onLogin={handleLogin} onNavigateToSignup={handleNavigateToSignup} onForgotPassword={handleForgotPassword} data-id="element-7" />}
            {currentScreen === 'bop-signup' && <BOPSignupFlow key="bop-signup" onBack={() => setCurrentScreen('login')} onComplete={handleSignupComplete} data-id="element-8" />}
            {currentScreen === 'forgot-password' && <ForgotPasswordScreen key="forgot-password" onBack={() => setCurrentScreen('login')} onComplete={() => setCurrentScreen('login')} data-id="element-9" />}
            {currentScreen === 'mpin' && <MPINScreen key="mpin" mode={mpinMode} pinType={mpinType} onModeChange={setMpinMode} onComplete={handleMPINComplete} onBack={handleMPINBack} data-id="element-10" />}
            {currentScreen === 'home' && <HomeScreen key="home" isNewUser={isNewUser} isMpinSet={isMpinSet} isCreditCardUser={isCreditCardUser} onNavigateToTransactions={handleNavigateToTransactions} onNavigateToSendMoney={handleNavigateToSendMoney} onNavigateToPayments={() => setCurrentScreen('payments')} onNavigateToMobileTopUp={handleNavigateToMobileTopUp} onNavigateToRaast={handleNavigateToRaast} onNavigateToCards={() => setCurrentScreen('cards')} onNavigateToProfile={handleNavigateToProfile} onNavigateToMore={handleNavigateToMore} onLogout={handleLogout} onSetMPIN={() => {
            setMpinMode('set');
            setMpinType('mpin');
            setCurrentScreen('mpin');
          }} data-id="element-11" />}
            {currentScreen === 'transactions' && <TransactionsScreen key="transactions" onBack={() => setCurrentScreen('home')} data-id="element-12" />}
            {currentScreen === 'send-money' && <SendMoneyScreen key="send-money" onBack={() => setCurrentScreen('home')} onNavigateHome={goHome} data-id="element-13" />}
            {currentScreen === 'mobile-topup' && <MobileTopUpScreen key="mobile-topup" onBack={goHome} onNavigateHome={goHome} data-id="element-14" />}
            {currentScreen === 'raast' && <RaastScreen key="raast" onClose={goHome} onNavigateHome={goHome} initialView={raastInitialView} data-id="element-15" />}
            {currentScreen === 'payments' && <PaymentsScreen key="payments" onBack={goHome} onNavigateHome={goHome} data-id="element-16" />}
            {currentScreen === 'cards' && <CardsScreen key="cards" onBack={goHome} onNavigateHome={goHome} data-id="element-17" />}
            {currentScreen === 'rewards' && <RewardsScreen key="rewards" onBack={goHome} onNavigateHome={goHome} data-id="element-18" />}
            {currentScreen === 'cooling-off' && <CoolingOffScreen key="cooling-off" coolingOffEndTime={coolingOffUntil} onBack={() => setCurrentScreen('login')} onContinue={() => {
            setCoolingOffUntil(null);
            // Credit-card signups land directly on the home dashboard variant.
            if (signupUserType === 'bop_credit_card') {
              setIsCreditCardUser(true);
              setCurrentScreen('home');
            } else {
              setCurrentScreen('login');
            }
          }} data-id="element-19" />}
            {currentScreen === 'profile' && <ProfileScreen key="profile" onBack={goHome} data-id="element-20" />}
            {currentScreen === 'more' && <ZakaatScreen key="more" onBack={goHome} onNavigateHome={goHome} data-id="element-21" />}
          </AnimatePresence>

          {showBottomNav && <BottomNav activeScreen={currentScreen as 'home' | 'payments' | 'cards' | 'rewards'} onNavigate={handleNavigate} onQRScan={handleNavigateToRaastQR} data-id="element-22" />}
        </div>
      </div>
    </LanguageProvider>;
}